import { useContext, useRef } from 'react';
import { Controller } from 'react-hook-form';
import get from 'lodash/get';

import { GlobalContext } from '@contexts';
import {
  ColorField,
  DatamodelItemField,
  DatamodelSubTypeField,
  DateField,
  DateTimeField,
  FieldWrapper,
  FormFieldset,
  HTMLField,
  ImageField,
  SelectField,
  ImageSelectField,
  RelatedModelList,
  SimpleHTMLField,
  TextAreaField,
  TextField,
  ToggleField,
  StepperField,
  BlogsSelect,
  AddressAutocompleteField,
  ListItemsField,
} from '@Forms/fields.jsx';
import {
  removeNewlines,
  slugifyDash,
  urlFormat,
  slugifyOnBlur,
} from '@Forms/formatters.js';
import { fieldIsVisible } from '@Forms/utils.js';

const modelFromQueries = (queries, relatedDataModelID) => {
  // from list of react queries that are complete, find
  // the one that matches the datamodel ID
  let match;
  queries.forEach((query) => {
    if (query.data?.id == relatedDataModelID) {
      match = query.data;
    }
  });
  return match;
};

export const DatamodelFormField = ({
  datamodelField,
  fieldPrefix = '',
  relatedDatamodels,
  setIsUploading,
  siteID,
  useFormReturn,
}) => {
  // Dynamic form fields based on datamodel field types
  //
  // siteID used for Image uploads to associate with media library

  // handling for tinymce fields
  const editorRefs = useRef({});

  // fieldPrefix: ability to prefix the field (i.e. "team_members.0." for nested field IDs)
  const fieldName = `${fieldPrefix}${datamodelField.slug}`;

  // unpacked form vars from useForm
  const {
    register,
    formState: { errors },
    getValues,
    control,
    setValue,
    watch,
  } = useFormReturn;

  // globals
  const globals = useContext(GlobalContext);
  // check if field should be suppressed/hidden
  if (
    globals &&
    globals.group_settings &&
    globals.group_settings.restrict_custom_fields
  ) {
    if (!fieldIsVisible(datamodelField, globals.group_ids)) {
      // field should not be visible, hide
      return;
    }
  }

  // property defaults (i.e. required or other validation options)
  const properties = {
    required: datamodelField.required ? 'This field is required' : false,
  };
  switch (datamodelField.field_type) {
    case 'boolean':
      // Remove the default required value passed to the options
      properties.required = false;
      return (
        <ToggleField
          register={register}
          control={control}
          error={get(errors, fieldName)}
          options={properties}
          name={fieldName}
          label={datamodelField.label}
          helpText={datamodelField.help_text}
        />
      );
    case 'rgba_color':
      return (
        <ColorField
          useFormReturn={useFormReturn}
          options={properties}
          name={fieldName}
          label={datamodelField.label}
          helpText={datamodelField.help_text}
        />
      );
    case 'datamodel_item':
      // suffix the fieldName with _id
      const idFieldName = `${fieldName}_id`;
      return (
        <FieldWrapper
          error={get(errors, idFieldName)}
          label={datamodelField.label}
          name={fieldName}
          helpText={datamodelField.help_text}
        >
          <Controller
            control={control}
            name={idFieldName}
            render={({ field, value, name, ref }) => (
              <DatamodelItemField
                field={field}
                datamodelID={datamodelField.related_datamodel_id}
                defaultDatamodelItemID={getValues(idFieldName)}
              />
            )}
          />
        </FieldWrapper>
      );
    case 'datamodel_sub_type':
      return (
        <FieldWrapper
          error={get(errors, fieldName)}
          label={datamodelField.label}
          name={fieldName}
          helpText={datamodelField.help_text}
        >
          <Controller
            control={control}
            name={fieldName}
            render={({ field }) => <DatamodelSubTypeField field={field} />}
          />
        </FieldWrapper>
      );
    case 'date_picker':
      // Date coming from server is an iso 'YYYY-MM-DD' string
      return (
        <Controller
          control={control}
          name={fieldName}
          rules={properties}
          id={fieldName}
          render={({ field: controllerField }) => (
            <DateField
              isClearable={!datamodelField.required}
              error={get(errors, fieldName)}
              field={controllerField}
              helpText={datamodelField.help_text}
              label={datamodelField.label}
              value={getValues(fieldName)}
            />
          )}
        />
      );
    case 'datetime_picker':
      return (
        <Controller
          control={control}
          name={fieldName}
          id={fieldName}
          render={({ field: controllerField }) => (
            <DateTimeField
              isClearable={!datamodelField.required}
              error={get(errors, fieldName)}
              field={controllerField}
              label={datamodelField.label}
              value={getValues(fieldName)}
              helpText={datamodelField.help_text}
            />
          )}
        />
      );
    case 'html':
      return (
        <FormFieldset legend={datamodelField.label}>
          <HTMLField
            register={register}
            control={control}
            editorRefs={editorRefs}
            initialValue={getValues(fieldName)}
            error={get(errors, fieldPrefix)}
            name={fieldName}
            helpText={datamodelField.help_text}
          />
        </FormFieldset>
      );
    case 'image':
      return (
        <ImageField
          label={datamodelField.label}
          name={fieldName}
          useFormReturn={useFormReturn}
          siteID={siteID}
          setIsUploading={setIsUploading}
          options={properties}
          altTagName={datamodelField.enable_alt_tag ? `${fieldName}_alt` : null}
          serializationType="flat"
          helpText={datamodelField.help_text}
        />
      );
    case 'image_select':
      return (
        <ImageSelectField
          choices={datamodelField.choices}
          currentValue={getValues(`${fieldName}_id`)}
          error={get(errors, fieldName)}
          helpText={datamodelField.help_text}
          label={datamodelField.label}
          name={fieldName}
          options={properties}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      );
    case 'long_text':
      return (
        <TextField
          register={register}
          error={get(errors, fieldName)}
          options={properties}
          name={fieldName}
          label={datamodelField.label}
          placeholder={datamodelField.placeholder}
          helpText={datamodelField.help_text}
        />
      );
    case 'multi_line_text':
      // dont allow newlines
      properties.onChange = removeNewlines;
      return (
        <TextAreaField
          register={register}
          options={properties}
          error={get(errors, fieldName)}
          name={fieldName}
          label={datamodelField.label}
          helpText={datamodelField.help_text}
        />
      );
    case 'number':
      properties.valueAsNumber = true;
      return (
        <TextField
          type="number"
          register={register}
          options={properties}
          error={get(errors, fieldName)}
          name={fieldName}
          label={datamodelField.label}
          helpText={datamodelField.help_text}
        />
      );
    case 'related_model_list':
      const relatedDataModel = modelFromQueries(
        relatedDatamodels,
        datamodelField.related_datamodel_id,
      );
      return (
        <FieldWrapper
          label={datamodelField.label}
          name={fieldName}
          helpText={datamodelField.help_text}
        >
          <div className="sb-form-item-active">
            <RelatedModelList
              fieldName={fieldName}
              datamodelField={datamodelField}
              relatedDataModel={relatedDataModel}
              siteID={siteID}
              useFormReturn={useFormReturn}
            />
          </div>
        </FieldWrapper>
      );
    case 'stepper':
      return (
        <StepperField
          control={control}
          error={get(errors, fieldName)}
          name={fieldName}
          rules={properties}
        />
      );
    case 'select':
      let choices = datamodelField.choices.map((choice) => {
        return (
          <option key={choice.val} val={choice.val}>
            {choice.label}
          </option>
        );
      });
      return (
        <SelectField
          register={register}
          error={get(errors, fieldName)}
          options={properties}
          name={fieldName}
          label={datamodelField.label}
          helpText={datamodelField.help_text}
        >
          <option value="">Select an option...</option>
          {choices}
        </SelectField>
      );
    case 'short_text':
      return (
        <TextField
          register={register}
          error={get(errors, fieldName)}
          options={properties}
          name={fieldName}
          label={datamodelField.label}
          placeholder={datamodelField.placeholder}
          helpText={datamodelField.help_text}
        />
      );
    case 'simple_html':
      return (
        <SimpleHTMLField
          register={register}
          control={control}
          editorRefs={editorRefs}
          initialValue={getValues(fieldName)}
          error={get(errors, fieldName)}
          name={fieldName}
          label={datamodelField.label}
          helpText={datamodelField.help_text}
        />
      );
    case 'slug':
      properties.onChange = slugifyDash;
      properties.onBlur = slugifyOnBlur;
      return (
        <TextField
          register={register}
          options={properties}
          error={get(errors, fieldName)}
          name={fieldName}
          label={datamodelField.label}
          helpText={datamodelField.help_text}
        />
      );
    case 'url':
      properties.onChange = urlFormat;
      return (
        <TextField
          register={register}
          options={properties}
          error={get(errors, fieldName)}
          name={fieldName}
          label={datamodelField.label}
          helpText={datamodelField.help_text}
        />
      );
    case 'blog_post_select':
      return (
        <FieldWrapper
          error={get(errors, fieldName)}
          label={datamodelField.label}
          name={fieldName}
          helpText={datamodelField.help_text}
        >
          <Controller
            control={control}
            name={fieldName}
            rules={properties}
            render={({ field }) => (
              <BlogsSelect
                siteID={siteID}
                field={field}
                defaultBlogs={getValues(fieldName)}
              />
            )}
          />
        </FieldWrapper>
      );
    case 'address_autocomplete': {
      const addressOptions = datamodelField.required
        ? {
            validate: (value) =>
              value?.formatted_address ? true : 'This field is required',
          }
        : {};
      return (
        <AddressAutocompleteField
          control={control}
          options={addressOptions}
          name={fieldName}
          label={datamodelField.label}
          helpText={datamodelField.help_text}
          placeholder={datamodelField.placeholder}
          autocompleteOptions={datamodelField.options?.autocomplete_options}
        />
      );
    }
    case 'item_list':
      return (
        <ListItemsField
          register={register}
          control={control}
          error={get(errors, fieldName)}
          name={fieldName}
          options={properties}
          label={datamodelField.label}
          helpText={datamodelField.help_text}
          setValue={setValue}
        />
      );
    default:
      return (
        <div key={fieldName}>
          <label>
            {datamodelField.label} {datamodelField.field_type}
          </label>
        </div>
      );
  }
};
